import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft, ChefHat, Plus, Trash2, Edit3, X, Save, ClipboardList, BarChart3, Users as UsersIcon,
  BookOpen, Receipt, Minus, MessageCircle, CheckCircle2, Package, Share2, ChevronLeft, ChevronRight,
  KeyRound, Copy, AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useBranding } from "./BrandingContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function Kitchen({ onBack }) {
  const branding = useBranding();
  const [tab, setTab] = useState("billing");
  const [lowCount, setLowCount] = useState(0);
  useEffect(() => {
    axios.get(`${API}/kitchen/products/low-stock`)
      .then((r) => setLowCount(r.data.count || 0))
      .catch(() => {});
  }, [tab]);
  const tabs = [
    { id: "billing", label: "Billing", icon: Receipt },
    { id: "orders", label: "Orders", icon: ClipboardList },
    { id: "menu", label: "Menu", icon: BookOpen },
    { id: "stock", label: "Stock", icon: Package },
    { id: "suppliers", label: "Suppliers", icon: UsersIcon },
    { id: "reports", label: "Reports", icon: BarChart3 },
  ];

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      <div className="bg-slate-900 border-b border-amber-700/30 px-6 py-4 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}
            className="rounded-full text-amber-300 hover:text-amber-400 hover:bg-amber-900/30"
            data-testid="kitchen-back-button">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-bold tracking-[0.18em] text-amber-400 truncate">
              {branding.club_name} · KITCHEN
            </h1>
            <p className="text-xs text-amber-200/70 flex items-center gap-1">
              <ChefHat className="w-3 h-3" /> Menu, billing, orders &amp; reports
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white border-b border-slate-200 sticky top-[65px] z-10">
        <div className="max-w-5xl mx-auto flex overflow-x-auto no-scrollbar">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              data-testid={`kitchen-tab-${t.id}`}
              className={`flex-shrink-0 flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id ? "border-amber-500 text-amber-700" : "border-transparent text-slate-500 hover:text-slate-800"
              }`}>
              <t.icon className="w-4 h-4" />
              {t.label}
              {t.id === "stock" && lowCount > 0 && (
                <span className="ml-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
                  data-testid="low-stock-badge">{lowCount}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {tab === "billing" && <BillingTab />}
        {tab === "orders" && <OrdersTab />}
        {tab === "menu" && <MenuTab />}
        {tab === "stock" && <StockTab />}
        {tab === "suppliers" && <SuppliersTab />}
        {tab === "reports" && <KitchenReportsTab />}
      </div>
    </div>
  );
}

// ============ BILLING (order entry with menu items) ============
function BillingTab() {
  const [suppliers, setSuppliers] = useState([]);
  const [menu, setMenu] = useState([]);
  const [products, setProducts] = useState([]);
  const [source, setSource] = useState("direct");
  const [cart, setCart] = useState([]);
  const [itemSearch, setItemSearch] = useState("");
  const [cash, setCash] = useState("");
  const [gpay, setGpay] = useState("");
  const [card, setCard] = useState("");
  const [commission, setCommission] = useState("");
  const [note, setNote] = useState("");
  const [date, setDate] = useState(todayISO());
  const [recent, setRecent] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadSuppliers = useCallback(async () => {
    const { data } = await axios.get(`${API}/kitchen/suppliers?active_only=true`);
    setSuppliers(data.suppliers || []);
  }, []);
  const loadMenu = useCallback(async () => {
    const { data } = await axios.get(`${API}/kitchen/menu?active_only=true`);
    setMenu(data.items || []);
  }, []);
  const loadProducts = useCallback(async () => {
    const { data } = await axios.get(`${API}/kitchen/products?active_only=true`);
    setProducts(data.products || []);
  }, []);
  const loadRecent = useCallback(async () => {
    const { data } = await axios.get(`${API}/kitchen/sales?limit=10`);
    setRecent(data.sales || []);
  }, []);
  useEffect(() => { loadSuppliers(); loadMenu(); loadProducts(); loadRecent(); }, [loadSuppliers, loadMenu, loadProducts, loadRecent]);

  const itemsTotal = useMemo(
    () => cart.reduce((a, l) => a + (Number(l.qty) || 0) * (Number(l.price) || 0), 0),
    [cart]
  );
  const payTotal = useMemo(
    () => (Number(cash) || 0) + (Number(gpay) || 0) + (Number(card) || 0),
    [cash, gpay, card]
  );
  const remaining = Math.round((itemsTotal - payTotal) * 100) / 100;

  // Auto-calc commission from slab for supplier orders (editable)
  useEffect(() => {
    if (source === "direct") { setCommission(""); return; }
    const s = suppliers.find((x) => x.id === source);
    if (!s) return;
    const slab = Number(s.commission_slab_amount) || 0;
    const per = Number(s.commission_per_slab) || 0;
    const base = payTotal > 0 ? payTotal : itemsTotal;
    if (slab > 0 && per > 0 && base > 0) {
      setCommission(String(Math.floor(base / slab) * per));
    } else if (slab > 0 && per > 0) {
      setCommission("0");
    } else if (s.default_commission) {
      setCommission(String(s.default_commission));
    }
  }, [source, payTotal, itemsTotal, suppliers]);

  const addItem = (m, itemType = "menu") => {
    setCart((c) => {
      const idx = c.findIndex((l) => l.menu_item_id === m.id);
      if (idx >= 0) {
        const copy = [...c];
        copy[idx] = { ...copy[idx], qty: Number(copy[idx].qty) + 1 };
        return copy;
      }
      if (itemType === "stock") {
        return [...c, { menu_item_id: m.id, item_type: "stock", name: m.name, qty: 1, price: m.sale_rate, cost_price: m.purchase_rate }];
      }
      return [...c, { menu_item_id: m.id, item_type: "menu", name: m.name, qty: 1, price: m.selling_price, cost_price: m.cost_price }];
    });
  };
  const updateLine = (i, patch) => setCart((c) => c.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const removeLine = (i) => setCart((c) => c.filter((_, idx) => idx !== i));

  const fillAll = (which) => {
    const t = String(itemsTotal || payTotal || "");
    setCash(which === "cash" ? t : "");
    setGpay(which === "gpay" ? t : "");
    setCard(which === "card" ? t : "");
  };

  const submit = async () => {
    if (cart.length === 0) { toast.error("Add at least one menu item"); return; }
    if (payTotal <= 0) { toast.error("Record payment (Cash/GPay/Card)"); return; }
    if (Math.abs(remaining) > 0.01) {
      toast.error(`Payments (${INR(payTotal)}) must equal bill total (${INR(itemsTotal)})`);
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/kitchen/sales`, {
        supplier_id: source === "direct" ? "direct" : source,
        items: cart.map((l) => ({
          menu_item_id: l.menu_item_id, item_type: l.item_type || "menu", name: l.name,
          qty: Number(l.qty) || 0, price: Number(l.price) || 0, cost_price: Number(l.cost_price) || 0,
        })),
        cash: Number(cash) || 0,
        gpay: Number(gpay) || 0,
        card: Number(card) || 0,
        commission: Number(commission) || 0,
        note,
        date,
      });
      toast.success(`Order ${data.order_number} saved (${INR(data.total)})`);
      setCart([]); setCash(""); setGpay(""); setCard(""); setCommission(""); setNote("");
      loadRecent();
      loadProducts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const filteredMenu = menu.filter((m) => m.name.toLowerCase().includes(itemSearch.toLowerCase()));
  const filteredProducts = products.filter((p) =>
    p.name.toLowerCase().includes(itemSearch.toLowerCase()) ||
    (p.barcode || "").toLowerCase().includes(itemSearch.toLowerCase())
  );

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
      <div className="space-y-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
          <h3 className="text-lg font-bold text-slate-900">New Order / Bill</h3>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wider">Order From</Label>
              <select value={source} onChange={(e) => setSource(e.target.value)}
                className="w-full h-11 px-3 rounded-xl border border-slate-200 bg-slate-50 text-sm mt-1"
                data-testid="kitchen-source-select">
                <option value="direct">Direct Sales (counter)</option>
                {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wider">Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-11 mt-1" data-testid="kitchen-sale-date" />
            </div>
          </div>

          {/* Menu picker */}
          <div>
            <Label className="text-xs text-slate-500 uppercase tracking-wider">Add Items</Label>
            <Input value={itemSearch} onChange={(e) => setItemSearch(e.target.value)} placeholder="Search food & stock..."
              className="h-10 mt-1 mb-2" data-testid="kitchen-menu-search" />
            <div className="flex flex-wrap gap-2 max-h-44 overflow-y-auto">
              {filteredMenu.length === 0 && filteredProducts.length === 0 && (
                <span className="text-xs text-slate-400 py-2">No items — add them in the Menu or Stock tab first.</span>
              )}
              {filteredMenu.map((m) => (
                <button key={m.id} onClick={() => addItem(m, "menu")}
                  className="px-3 py-1.5 rounded-full border border-amber-300 bg-amber-50 text-amber-800 text-xs font-semibold hover:bg-amber-100 transition-colors"
                  data-testid={`menu-pick-${m.id}`}>
                  {m.name} · {INR(m.selling_price)}
                </button>
              ))}
              {filteredProducts.map((p) => (
                <button key={p.id} onClick={() => addItem(p, "stock")} disabled={(p.current_stock || 0) <= 0}
                  className={`px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors ${
                    (p.current_stock || 0) <= 0
                      ? "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed"
                      : "border-violet-300 bg-violet-50 text-violet-800 hover:bg-violet-100"
                  }`}
                  data-testid={`stock-pick-${p.id}`}
                  title={`Stock: ${p.current_stock || 0}`}>
                  {p.name} · {INR(p.sale_rate)}{(p.current_stock || 0) <= 0 ? " · OUT" : ""}
                </button>
              ))}
            </div>
          </div>

          {/* Cart */}
          {cart.length > 0 && (
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Item</th>
                    <th className="px-2 py-2 text-center">Qty</th>
                    <th className="px-2 py-2 text-right">Price ₹</th>
                    <th className="px-2 py-2 text-right">Total</th>
                    <th className="px-2 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {cart.map((l, i) => (
                    <tr key={i} className="border-t border-slate-100" data-testid={`cart-line-${i}`}>
                      <td className="px-3 py-2 font-medium text-slate-800">{l.name}</td>
                      <td className="px-2 py-2">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => updateLine(i, { qty: Math.max(1, Number(l.qty) - 1) })}
                            className="w-6 h-6 rounded-full border border-slate-300 flex items-center justify-center text-slate-600 hover:bg-slate-100"
                            data-testid={`cart-qty-minus-${i}`}><Minus className="w-3 h-3" /></button>
                          <span className="w-7 text-center font-semibold" data-testid={`cart-qty-${i}`}>{l.qty}</span>
                          <button onClick={() => updateLine(i, { qty: Number(l.qty) + 1 })}
                            className="w-6 h-6 rounded-full border border-slate-300 flex items-center justify-center text-slate-600 hover:bg-slate-100"
                            data-testid={`cart-qty-plus-${i}`}><Plus className="w-3 h-3" /></button>
                        </div>
                      </td>
                      <td className="px-2 py-2 text-right">
                        <Input type="number" value={l.price}
                          onChange={(e) => updateLine(i, { price: e.target.value })}
                          className="h-8 w-20 text-right ml-auto" data-testid={`cart-price-${i}`} />
                      </td>
                      <td className="px-2 py-2 text-right font-semibold">{INR((Number(l.qty) || 0) * (Number(l.price) || 0))}</td>
                      <td className="px-2 py-2 text-right">
                        <button onClick={() => removeLine(i)} className="text-red-400 hover:text-red-600" data-testid={`cart-remove-${i}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Payments */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs text-slate-500 uppercase tracking-wider">Payment Split</Label>
              <div className="flex gap-1">
                <button onClick={() => fillAll("cash")} className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold" data-testid="fill-all-cash">All Cash</button>
                <button onClick={() => fillAll("gpay")} className="text-[10px] px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200 font-semibold" data-testid="fill-all-gpay">All GPay</button>
                <button onClick={() => fillAll("card")} className="text-[10px] px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200 font-semibold" data-testid="fill-all-card">All Card</button>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-[10px] text-slate-500 uppercase">Cash ₹</Label>
                <Input type="number" value={cash} onChange={(e) => setCash(e.target.value)} placeholder="0" className="h-11 mt-1" data-testid="kitchen-cash-input" />
              </div>
              <div>
                <Label className="text-[10px] text-slate-500 uppercase">GPay ₹</Label>
                <Input type="number" value={gpay} onChange={(e) => setGpay(e.target.value)} placeholder="0" className="h-11 mt-1" data-testid="kitchen-gpay-input" />
              </div>
              <div>
                <Label className="text-[10px] text-slate-500 uppercase">Card ₹</Label>
                <Input type="number" value={card} onChange={(e) => setCard(e.target.value)} placeholder="0" className="h-11 mt-1" data-testid="kitchen-card-input" />
              </div>
            </div>
            {cart.length > 0 && Math.abs(remaining) > 0.01 && (
              <p className="text-[11px] mt-1 font-semibold text-red-600" data-testid="payment-remaining-warning">
                {remaining > 0 ? `₹${remaining} remaining to collect` : `₹${Math.abs(remaining)} extra entered`}
              </p>
            )}
          </div>

          {source !== "direct" && (
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wider">Commission ₹ (auto from slab, editable)</Label>
              <Input type="number" value={commission} onChange={(e) => setCommission(e.target.value)} placeholder="0" className="h-11 mt-1" data-testid="kitchen-commission-input" />
            </div>
          )}

          <div>
            <Label className="text-xs text-slate-500 uppercase tracking-wider">Note</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional" className="h-11 mt-1" data-testid="kitchen-note-input" />
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-slate-100">
            <div>
              <div className="text-xs text-slate-500">Bill Total</div>
              <div className="text-2xl font-bold text-slate-900" data-testid="kitchen-total-display">{INR(itemsTotal)}</div>
            </div>
            <Button onClick={submit} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white h-11 px-6" data-testid="save-kitchen-sale-button">
              <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Order"}
            </Button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-4">
        <h3 className="font-semibold text-slate-900 mb-3">Recent Orders</h3>
        <div className="space-y-2 max-h-[600px] overflow-y-auto">
          {recent.length === 0 && <div className="text-center text-sm text-slate-400 py-8">No orders yet</div>}
          {recent.map((s) => (
            <div key={s.id} className="border border-slate-100 rounded-xl p-3" data-testid={`kitchen-recent-${s.id}`}>
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <div className="font-semibold text-sm text-slate-900 truncate">{s.order_number || s.supplier_name}</div>
                  <div className="text-xs text-slate-500">{s.supplier_name} · {s.date}</div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-slate-900">{INR(s.total)}</div>
                  <StatusBadge status={s.status} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  if (status === "ready") {
    return <span className="inline-block text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">READY</span>;
  }
  return <span className="inline-block text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">PENDING</span>;
}

// ============ ORDERS (status + WhatsApp notify) ============
function OrdersTab() {
  const [date, setDate] = useState(todayISO());
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [editing, setEditing] = useState(null);

  const shiftDate = (delta) => {
    const d = new Date(date + "T00:00:00");
    d.setDate(d.getDate() + delta);
    setDate(d.toISOString().slice(0, 10));
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/kitchen/sales?date_from=${date}&date_to=${date}&limit=300`);
      setOrders(data.sales || []);
    } catch (e) { toast.error("Failed to load orders"); }
    finally { setLoading(false); }
  }, [date]);
  useEffect(() => { load(); }, [load]);

  const markReady = async (order) => {
    setBusy(order.id);
    try {
      const { data } = await axios.patch(`${API}/kitchen/sales/${order.id}/ready`);
      if (data.whatsapp_url) {
        window.open(data.whatsapp_url, "_blank");
        toast.success(`Order ${order.order_number || ""} ready — WhatsApp opened for supplier`);
      } else if (order.is_direct) {
        toast.success(`Order ${order.order_number || ""} marked ready`);
      } else {
        toast.warning("Marked ready, but supplier has no phone number saved");
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };

  const shareReceipt = async (order) => {
    try {
      const { data } = await axios.get(`${API}/kitchen/sales/${order.id}/receipt`);
      window.open(data.whatsapp_url, "_blank");
    } catch (e) { toast.error("Failed to build receipt"); }
  };

  const del = async (order) => {
    if (!window.confirm(`Delete order ${order.order_number || ""}?`)) return;
    try {
      await axios.delete(`${API}/kitchen/sales/${order.id}`);
      toast.success("Order deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed (admin only)"); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-slate-200 p-2 flex items-center justify-between gap-2">
        <button onClick={() => shiftDate(-1)} className="p-2 rounded-xl hover:bg-slate-100 text-slate-600" data-testid="orders-date-prev">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-10 w-40 border-0 font-semibold text-center" data-testid="orders-date-filter" />
          <span className="text-xs text-slate-400">{orders.length} orders</span>
        </div>
        <button onClick={() => shiftDate(1)} className="p-2 rounded-xl hover:bg-slate-100 text-slate-600" data-testid="orders-date-next">
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {loading && <div className="text-center text-slate-400 py-10">Loading...</div>}
      {!loading && orders.length === 0 && <div className="text-center text-slate-400 py-10 text-sm">No orders on this date</div>}

      <div className="grid gap-3 sm:grid-cols-2">
        {orders.map((o) => (
          <div key={o.id} className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={`order-card-${o.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-bold text-slate-900">{o.order_number || "—"}</div>
                <div className="text-xs mt-0.5">
                  {o.is_direct
                    ? <span className="px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 font-semibold text-[10px]">DIRECT SALES</span>
                    : <span className="px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 font-semibold text-[10px]">{o.supplier_name}</span>}
                </div>
              </div>
              <div className="text-right">
                <div className="font-bold text-slate-900">{INR(o.total)}</div>
                <StatusBadge status={o.status} />
              </div>
            </div>

            {(o.items || []).length > 0 && (
              <div className="mt-2 text-xs text-slate-600 space-y-0.5">
                {o.items.map((it, i) => (
                  <div key={i} className="flex justify-between">
                    <span>{it.qty} × {it.name}</span>
                    <span>{INR((it.qty || 0) * (it.price || 0))}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-3 mt-2 text-[10px] text-slate-400">
              {o.cash > 0 && <span>Cash: {INR(o.cash)}</span>}
              {o.gpay > 0 && <span>GPay: {INR(o.gpay)}</span>}
              {o.card > 0 && <span>Card: {INR(o.card)}</span>}
              {o.commission > 0 && <span className="text-amber-600">Comm: {INR(o.commission)}</span>}
            </div>

            <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
              {o.status !== "ready" ? (
                <Button size="sm" onClick={() => markReady(o)} disabled={busy === o.id}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white h-9 rounded-lg"
                  data-testid={`mark-ready-${o.id}`}>
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                  {o.is_direct ? "Mark Ready" : "Ready + Notify"}
                </Button>
              ) : !o.is_direct ? (
                <Button size="sm" variant="outline" onClick={() => markReady(o)} disabled={busy === o.id}
                  className="flex-1 h-9 rounded-lg border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  data-testid={`renotify-${o.id}`}>
                  <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp Again
                </Button>
              ) : (
                <div className="flex-1 flex items-center justify-center text-xs text-emerald-600 font-semibold">✓ Completed</div>
              )}
              <Button size="sm" variant="ghost" onClick={() => del(o)}
                className="h-9 rounded-lg text-red-500 hover:bg-red-50" data-testid={`delete-order-${o.id}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>

            <div className="flex mt-2 pt-2 border-t border-slate-100 divide-x divide-slate-100">
              <button onClick={() => setEditing(o)}
                className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold text-slate-600 hover:text-amber-700 transition-colors"
                data-testid={`edit-bill-${o.id}`}>
                <Edit3 className="w-3.5 h-3.5" /> Edit Bill
              </button>
              <button onClick={() => shareReceipt(o)}
                className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold text-slate-600 hover:text-emerald-700 transition-colors"
                data-testid={`share-receipt-${o.id}`}>
                <Share2 className="w-3.5 h-3.5" /> Share Receipt
              </button>
            </div>
          </div>
        ))}
      </div>

      <AnimatePresence>
        {editing && (
          <EditBillModal order={editing} onClose={() => setEditing(null)} onSaved={load} />
        )}
      </AnimatePresence>
    </div>
  );
}

// ============ EDIT BILL ============
function EditBillModal({ order, onClose, onSaved }) {
  const [suppliers, setSuppliers] = useState([]);
  const [menu, setMenu] = useState([]);
  const [products, setProducts] = useState([]);
  const [source, setSource] = useState(order.is_direct ? "direct" : order.supplier_id);
  const [cart, setCart] = useState((order.items || []).map((i) => ({ ...i })));
  const [itemSearch, setItemSearch] = useState("");
  const [cash, setCash] = useState(order.cash || "");
  const [gpay, setGpay] = useState(order.gpay || "");
  const [card, setCard] = useState(order.card || "");
  const [commission, setCommission] = useState(order.commission || "");
  const [note, setNote] = useState(order.note || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/kitchen/suppliers?active_only=true`).then((r) => setSuppliers(r.data.suppliers || []));
    axios.get(`${API}/kitchen/menu?active_only=true`).then((r) => setMenu(r.data.items || []));
    axios.get(`${API}/kitchen/products?active_only=true`).then((r) => setProducts(r.data.products || []));
  }, []);

  const itemsTotal = cart.reduce((a, l) => a + (Number(l.qty) || 0) * (Number(l.price) || 0), 0);
  const payTotal = (Number(cash) || 0) + (Number(gpay) || 0) + (Number(card) || 0);
  const remaining = Math.round((itemsTotal - payTotal) * 100) / 100;

  const addItem = (m, itemType = "menu") => {
    setCart((c) => {
      const idx = c.findIndex((l) => l.menu_item_id === m.id);
      if (idx >= 0) return c.map((l, i) => (i === idx ? { ...l, qty: Number(l.qty) + 1 } : l));
      if (itemType === "stock") return [...c, { menu_item_id: m.id, item_type: "stock", name: m.name, qty: 1, price: m.sale_rate, cost_price: m.purchase_rate }];
      return [...c, { menu_item_id: m.id, item_type: "menu", name: m.name, qty: 1, price: m.selling_price, cost_price: m.cost_price }];
    });
  };
  const updateLine = (i, patch) => setCart((c) => c.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const removeLine = (i) => setCart((c) => c.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (cart.length === 0) { toast.error("Bill needs at least one item"); return; }
    if (payTotal <= 0) { toast.error("Record payment (Cash/GPay/Card)"); return; }
    if (Math.abs(remaining) > 0.01) { toast.error(`Payments (${INR(payTotal)}) must equal bill total (${INR(itemsTotal)})`); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/kitchen/sales/${order.id}`, {
        supplier_id: source === "direct" ? "direct" : source,
        items: cart.map((l) => ({
          menu_item_id: l.menu_item_id || "", item_type: l.item_type || "menu", name: l.name,
          qty: Number(l.qty) || 0, price: Number(l.price) || 0, cost_price: Number(l.cost_price) || 0,
        })),
        cash: Number(cash) || 0, gpay: Number(gpay) || 0, card: Number(card) || 0,
        commission: Number(commission) || 0, note,
      });
      toast.success(`Bill ${order.order_number || ""} updated`);
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setSaving(false); }
  };

  const fm = menu.filter((m) => m.name.toLowerCase().includes(itemSearch.toLowerCase()));
  const fp = products.filter((p) => p.name.toLowerCase().includes(itemSearch.toLowerCase()));

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 sticky top-0 bg-white z-10">
          <h3 className="font-semibold">Edit Bill — {order.order_number}</h3>
          <Button size="icon" variant="ghost" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <Label className="text-xs uppercase text-slate-500">Order From</Label>
            <select value={source} onChange={(e) => setSource(e.target.value)}
              className="w-full h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 text-sm mt-1"
              data-testid="edit-source-select">
              <option value="direct">Direct Sales (counter)</option>
              {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">Add Items</Label>
            <Input value={itemSearch} onChange={(e) => setItemSearch(e.target.value)} placeholder="Search food & stock..." className="h-9 mt-1 mb-2" />
            <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
              {fm.map((m) => (
                <button key={m.id} onClick={() => addItem(m, "menu")}
                  className="px-2.5 py-1 rounded-full border border-amber-300 bg-amber-50 text-amber-800 text-[11px] font-semibold hover:bg-amber-100">
                  {m.name} · {INR(m.selling_price)}
                </button>
              ))}
              {fp.map((p) => (
                <button key={p.id} onClick={() => addItem(p, "stock")}
                  className="px-2.5 py-1 rounded-full border border-violet-300 bg-violet-50 text-violet-800 text-[11px] font-semibold hover:bg-violet-100">
                  {p.name} · {INR(p.sale_rate)}
                </button>
              ))}
            </div>
          </div>
          <div className="border border-slate-200 rounded-xl divide-y divide-slate-100">
            {cart.map((l, i) => (
              <div key={i} className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`edit-cart-line-${i}`}>
                <span className="flex-1 font-medium truncate">{l.name}</span>
                <button onClick={() => updateLine(i, { qty: Math.max(1, Number(l.qty) - 1) })}
                  className="w-6 h-6 rounded-full border border-slate-300 flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                <span className="w-6 text-center font-semibold">{l.qty}</span>
                <button onClick={() => updateLine(i, { qty: Number(l.qty) + 1 })}
                  className="w-6 h-6 rounded-full border border-slate-300 flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                <Input type="number" value={l.price} onChange={(e) => updateLine(i, { price: e.target.value })} className="h-8 w-20 text-right" />
                <button onClick={() => removeLine(i)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            {cart.length === 0 && <div className="text-center text-xs text-slate-400 py-4">No items on bill</div>}
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-[10px] uppercase text-slate-500">Cash ₹</Label>
              <Input type="number" value={cash} onChange={(e) => setCash(e.target.value)} className="h-10 mt-1" data-testid="edit-cash-input" /></div>
            <div><Label className="text-[10px] uppercase text-slate-500">GPay ₹</Label>
              <Input type="number" value={gpay} onChange={(e) => setGpay(e.target.value)} className="h-10 mt-1" data-testid="edit-gpay-input" /></div>
            <div><Label className="text-[10px] uppercase text-slate-500">Card ₹</Label>
              <Input type="number" value={card} onChange={(e) => setCard(e.target.value)} className="h-10 mt-1" data-testid="edit-card-input" /></div>
          </div>
          {Math.abs(remaining) > 0.01 && (
            <p className="text-[11px] font-semibold text-red-600">
              {remaining > 0 ? `₹${remaining} remaining to collect` : `₹${Math.abs(remaining)} extra entered`} — bill total {INR(itemsTotal)}
            </p>
          )}
          {source !== "direct" && (
            <div><Label className="text-[10px] uppercase text-slate-500">Commission ₹</Label>
              <Input type="number" value={commission} onChange={(e) => setCommission(e.target.value)} className="h-10 mt-1" data-testid="edit-commission-input" /></div>
          )}
          <div><Label className="text-[10px] uppercase text-slate-500">Note</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} className="h-10 mt-1" /></div>
          <div className="flex items-center justify-between pt-3 border-t border-slate-100">
            <div className="text-lg font-bold">{INR(itemsTotal)}</div>
            <Button onClick={submit} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-edit-bill-button">
              <Save className="w-4 h-4 mr-1" /> {saving ? "Saving..." : "Update Bill"}
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============ MENU (common items with cost/selling/profit) ============
function MenuTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/kitchen/menu`);
    setItems(data.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setCreating(true)} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="add-menu-item-button">
          <Plus className="w-4 h-4 mr-1" /> Add Menu Item
        </Button>
      </div>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((m) => (
          <div key={m.id} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`menu-item-card-${m.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-semibold text-slate-900 truncate">{m.name}</div>
                <div className="text-xs text-slate-500 mt-1">
                  Cost: {INR(m.cost_price)} · Sell: <b className="text-slate-800">{INR(m.selling_price)}</b>
                </div>
                <div className={`text-xs font-semibold mt-0.5 ${m.profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                  Profit: {INR(m.profit)}
                </div>
                {!m.is_active && <span className="inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700">INACTIVE</span>}
              </div>
              <Button size="sm" variant="outline" onClick={() => setEditing(m)} data-testid={`edit-menu-item-${m.id}`}>
                <Edit3 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="col-span-full text-center text-slate-400 py-8 text-sm">No menu items yet — click Add Menu Item.</div>
        )}
      </div>
      <AnimatePresence>
        {creating && <MenuItemModal item={null} onClose={() => setCreating(false)} onSaved={load} />}
        {editing && <MenuItemModal item={editing} onClose={() => setEditing(null)} onSaved={load} />}
      </AnimatePresence>
    </div>
  );
}

function MenuItemModal({ item, onClose, onSaved }) {
  const isNew = !item;
  const [form, setForm] = useState({
    name: item?.name || "",
    cost_price: item?.cost_price ?? "",
    selling_price: item?.selling_price ?? "",
    is_active: item?.is_active !== false,
  });
  const [saving, setSaving] = useState(false);
  const profit = (Number(form.selling_price) || 0) - (Number(form.cost_price) || 0);
  const submit = async () => {
    if (!form.name.trim()) { toast.error("Item name required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        cost_price: Number(form.cost_price) || 0,
        selling_price: Number(form.selling_price) || 0,
        is_active: form.is_active,
      };
      if (isNew) await axios.post(`${API}/kitchen/menu`, payload);
      else await axios.patch(`${API}/kitchen/menu/${item.id}`, payload);
      toast.success(isNew ? "Menu item added" : "Menu item updated");
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const del = async () => {
    if (!window.confirm(`Delete menu item "${item.name}"?`)) return;
    try {
      await axios.delete(`${API}/kitchen/menu/${item.id}`);
      toast.success("Deleted"); onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 sticky top-0 bg-white">
          <h3 className="font-semibold">{isNew ? "Add Menu Item" : "Edit Menu Item"}</h3>
          <Button size="icon" variant="ghost" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <Label className="text-xs uppercase text-slate-500">Item Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Chicken Biryani" data-testid="menu-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase text-slate-500">Production Cost ₹</Label>
              <Input type="number" value={form.cost_price} onChange={(e) => setForm({ ...form, cost_price: e.target.value })} placeholder="0" data-testid="menu-cost-input" />
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-500">Selling Price ₹</Label>
              <Input type="number" value={form.selling_price} onChange={(e) => setForm({ ...form, selling_price: e.target.value })} placeholder="0" data-testid="menu-price-input" />
            </div>
          </div>
          <div className={`rounded-xl p-3 text-sm font-semibold ${profit >= 0 ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`} data-testid="menu-profit-preview">
            Profit per plate: {INR(profit)}
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="menu-active" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
            <label htmlFor="menu-active" className="text-sm">Active (shown in billing)</label>
          </div>
          <div className="flex gap-2 pt-3 border-t border-slate-100">
            {!isNew && <Button variant="outline" onClick={del} className="text-red-600 border-red-200" data-testid="delete-menu-item-button"><Trash2 className="w-4 h-4 mr-1" /> Delete</Button>}
            <div className="flex-1" />
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-menu-item-button">
              <Save className="w-4 h-4 mr-1" /> {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============ STOCK (kitchen inventory) ============
const DEFAULT_CATEGORIES = ["Cool Drinks", "Water Bottle", "Whisky", "Beer", "Vodka", "Snacks", "Cigarettes"];

function StockTab() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [lowItems, setLowItems] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/kitchen/products`, { params: { q, category: cat } });
    setProducts(data.products || []);
    setCategories(data.categories || []);
    const low = await axios.get(`${API}/kitchen/products/low-stock`);
    setLowItems(low.data.products || []);
  }, [q, cat]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const allCats = [...new Set([...categories, ...DEFAULT_CATEGORIES])];

  return (
    <div className="space-y-4">
      {lowItems.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4" data-testid="low-stock-panel">
          <div className="flex items-center gap-2 font-semibold text-red-800 text-sm mb-2">
            <AlertTriangle className="w-4 h-4" /> Running Low — refill needed ({lowItems.length})
          </div>
          <div className="space-y-1">
            {lowItems.map((p) => (
              <button key={p.id} onClick={() => setEditing(p)}
                className="w-full flex items-center justify-between text-left text-sm bg-white border border-red-100 rounded-xl px-3 py-2 hover:border-red-300 transition-colors"
                data-testid={`low-stock-item-${p.id}`}>
                <span className="font-medium text-slate-800 truncate">{p.name}</span>
                <span className="text-xs font-bold text-red-600 flex-shrink-0 ml-2">
                  {p.current_stock || 0} left (alert below {p.low_stock_below})
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / barcode..."
          className="h-11 flex-1" data-testid="stock-search-input" />
        <Button onClick={() => setCreating(true)} className="bg-amber-600 hover:bg-amber-700 text-white h-11" data-testid="add-product-button">
          <Plus className="w-4 h-4 mr-1" /> Add Product
        </Button>
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
        <button onClick={() => setCat("")}
          className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
            cat === "" ? "bg-amber-500 text-white border-amber-500" : "bg-white text-slate-600 border-slate-200 hover:border-amber-300"}`}
          data-testid="stock-cat-all">ALL</button>
        {allCats.map((c) => (
          <button key={c} onClick={() => setCat(c === cat ? "" : c)}
            className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              cat === c ? "bg-amber-500 text-white border-amber-500" : "bg-white text-slate-600 border-slate-200 hover:border-amber-300"}`}
            data-testid={`stock-cat-${c.replace(/\s+/g, "-")}`}>{c}</button>
        ))}
      </div>

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {products.map((p) => {
          const low = (p.current_stock || 0) < (p.low_stock_below ?? 5);
          return (
            <div key={p.id} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`product-card-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{p.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {p.category || "Uncategorised"}{p.size_ml ? ` · ${p.size_ml} ml` : ""}
                  </div>
                  <div className={`text-xs font-semibold mt-1 ${low ? "text-red-600" : "text-emerald-600"}`} data-testid={`product-stock-${p.id}`}>
                    Stock: {p.current_stock || 0}{low ? " ⚠ LOW" : ""}
                  </div>
                  {!p.is_active && <span className="inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700">INACTIVE</span>}
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="font-bold text-amber-600 text-lg">{INR(p.sale_rate)}</div>
                  {p.mrp > 0 && <div className="text-[10px] text-slate-400">MRP {INR(p.mrp)}</div>}
                  <Button size="sm" variant="outline" onClick={() => setEditing(p)} className="mt-1" data-testid={`edit-product-${p.id}`}>
                    <Edit3 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
        {products.length === 0 && (
          <div className="col-span-full text-center text-slate-400 py-8 text-sm">No products — click Add Product.</div>
        )}
      </div>

      <AnimatePresence>
        {creating && <ProductModal product={null} allCats={allCats} onClose={() => setCreating(false)} onSaved={load} />}
        {editing && <ProductModal product={editing} allCats={allCats} onClose={() => setEditing(null)} onSaved={load} />}
      </AnimatePresence>
    </div>
  );
}

function ProductModal({ product, allCats, onClose, onSaved }) {
  const isNew = !product;
  const [form, setForm] = useState({
    name: product?.name || "",
    barcode: product?.barcode || "",
    size_ml: product?.size_ml ?? "",
    category: product?.category || "",
    purchase_rate: product?.purchase_rate ?? "",
    sale_rate: product?.sale_rate ?? "",
    mrp: product?.mrp ?? "",
    current_stock: product?.current_stock ?? "",
    low_stock_below: product?.low_stock_below ?? 5,
    is_active: product?.is_active !== false,
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.name.trim()) { toast.error("Product name required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        barcode: form.barcode.trim(),
        size_ml: form.size_ml === "" ? null : Number(form.size_ml),
        category: form.category.trim(),
        purchase_rate: Number(form.purchase_rate) || 0,
        sale_rate: Number(form.sale_rate) || 0,
        mrp: Number(form.mrp) || 0,
        current_stock: Number(form.current_stock) || 0,
        low_stock_below: Number(form.low_stock_below) || 0,
        is_active: form.is_active,
      };
      if (isNew) await axios.post(`${API}/kitchen/products`, payload);
      else await axios.patch(`${API}/kitchen/products/${product.id}`, payload);
      toast.success(isNew ? "Product added" : "Product updated");
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const del = async () => {
    if (!window.confirm(`Delete product "${product.name}"?`)) return;
    try {
      await axios.delete(`${API}/kitchen/products/${product.id}`);
      toast.success("Deleted"); onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 sticky top-0 bg-white z-10">
          <h3 className="font-semibold">{isNew ? "Add Product" : "Edit Product"}</h3>
          <Button size="icon" variant="ghost" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <Label className="text-xs uppercase text-slate-500">Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Kinley Water 1L" data-testid="product-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase text-slate-500">Barcode</Label>
              <Input value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} placeholder="Optional" data-testid="product-barcode-input" />
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-500">Size (ml)</Label>
              <Input type="number" value={form.size_ml} onChange={(e) => setForm({ ...form, size_ml: e.target.value })} placeholder="e.g. 1000" data-testid="product-size-input" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">Category</Label>
            <div className="flex gap-1.5 overflow-x-auto no-scrollbar py-1.5">
              {allCats.map((c) => (
                <button key={c} onClick={() => setForm({ ...form, category: c })}
                  className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-semibold border ${
                    form.category === c ? "bg-amber-500 text-white border-amber-500" : "bg-white text-slate-600 border-slate-200"}`}>
                  {c}
                </button>
              ))}
            </div>
            <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
              placeholder="Or type a category" className="mt-1" data-testid="product-category-input" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-[10px] uppercase text-slate-500">Purchase ₹</Label>
              <Input type="number" value={form.purchase_rate} onChange={(e) => setForm({ ...form, purchase_rate: e.target.value })} placeholder="0" data-testid="product-purchase-input" /></div>
            <div><Label className="text-[10px] uppercase text-slate-500">Sale ₹</Label>
              <Input type="number" value={form.sale_rate} onChange={(e) => setForm({ ...form, sale_rate: e.target.value })} placeholder="0" data-testid="product-sale-input" /></div>
            <div><Label className="text-[10px] uppercase text-slate-500">MRP ₹</Label>
              <Input type="number" value={form.mrp} onChange={(e) => setForm({ ...form, mrp: e.target.value })} placeholder="0" data-testid="product-mrp-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-[10px] uppercase text-slate-500">Current Stock</Label>
              <Input type="number" value={form.current_stock} onChange={(e) => setForm({ ...form, current_stock: e.target.value })} placeholder="0" data-testid="product-stock-input" /></div>
            <div><Label className="text-[10px] uppercase text-slate-500">Low-stock alert below</Label>
              <Input type="number" value={form.low_stock_below} onChange={(e) => setForm({ ...form, low_stock_below: e.target.value })} data-testid="product-lowstock-input" /></div>
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="prod-active" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
            <label htmlFor="prod-active" className="text-sm">Active (available for sale)</label>
          </div>
          <div className="flex gap-2 pt-3 border-t border-slate-100">
            {!isNew && <Button variant="outline" onClick={del} className="text-red-600 border-red-200" data-testid="delete-product-button"><Trash2 className="w-4 h-4 mr-1" /> Delete</Button>}
            <div className="flex-1" />
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-product-button">
              <Save className="w-4 h-4 mr-1" /> {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============ SUPPLIERS ============
function SuppliersTab() {
  const [suppliers, setSuppliers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/kitchen/suppliers`);
    setSuppliers(data.suppliers || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setCreating(true)} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="add-supplier-button">
          <Plus className="w-4 h-4 mr-1" /> Add Supplier
        </Button>
      </div>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {suppliers.map((s) => (
          <div key={s.id} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`supplier-card-${s.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-semibold text-slate-900 truncate">{s.name}</div>
                {s.phone && <div className="text-xs text-slate-500">{s.phone}</div>}
                {s.commission_slab_amount > 0 && s.commission_per_slab > 0 ? (
                  <div className="text-xs text-amber-700 mt-1">
                    ₹{s.commission_per_slab} per ₹{s.commission_slab_amount}
                  </div>
                ) : s.default_commission > 0 ? (
                  <div className="text-xs text-amber-700 mt-1">Flat: {INR(s.default_commission)}</div>
                ) : (
                  <div className="text-xs text-slate-400 mt-1">No commission set</div>
                )}
                {!s.is_active && <span className="inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700">INACTIVE</span>}
              </div>
              <Button size="sm" variant="outline" onClick={() => setEditing(s)} data-testid={`edit-supplier-${s.id}`}>
                <Edit3 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        ))}
        {suppliers.length === 0 && (
          <div className="col-span-full text-center text-slate-400 py-8 text-sm">No suppliers yet — click Add Supplier to create one.</div>
        )}
      </div>
      <AnimatePresence>
        {creating && <SupplierEditModal supplier={null} onClose={() => setCreating(false)} onSaved={load} />}
        {editing && <SupplierEditModal supplier={editing} onClose={() => setEditing(null)} onSaved={load} />}
      </AnimatePresence>
    </div>
  );
}

function SupplierEditModal({ supplier, onClose, onSaved }) {
  const isNew = !supplier;
  const [form, setForm] = useState({
    name: supplier?.name || "",
    phone: supplier?.phone || "",
    commission_slab_amount: supplier?.commission_slab_amount || 0,
    commission_per_slab: supplier?.commission_per_slab || 0,
    default_commission: supplier?.default_commission || 0,
    note: supplier?.note || "",
    is_active: supplier?.is_active !== false,
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.name.trim()) { toast.error("Supplier name required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        commission_slab_amount: Number(form.commission_slab_amount) || 0,
        commission_per_slab: Number(form.commission_per_slab) || 0,
        default_commission: Number(form.default_commission) || 0,
      };
      if (isNew) await axios.post(`${API}/kitchen/suppliers`, payload);
      else await axios.patch(`${API}/kitchen/suppliers/${supplier.id}`, payload);
      toast.success(isNew ? "Supplier added" : "Supplier updated");
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const del = async () => {
    if (!window.confirm(`Delete supplier "${supplier.name}"?`)) return;
    try {
      await axios.delete(`${API}/kitchen/suppliers/${supplier.id}`);
      toast.success("Deleted"); onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const slabPreview = () => {
    const slab = Number(form.commission_slab_amount) || 0;
    const per = Number(form.commission_per_slab) || 0;
    if (slab <= 0 || per <= 0) return null;
    return `On ₹4,000 sale = ₹${Math.floor(4000 / slab) * per} commission`;
  };
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 sticky top-0 bg-white">
          <h3 className="font-semibold">{isNew ? "Add Supplier" : "Edit Supplier"}</h3>
          <Button size="icon" variant="ghost" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <Label className="text-xs uppercase text-slate-500">Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="supplier-name-input" />
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">Phone (WhatsApp — used for order notify)</Label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="10-digit mobile" data-testid="supplier-phone-input" />
          </div>
          <div className="border border-amber-200 bg-amber-50/40 rounded-xl p-3 space-y-2">
            <div className="text-xs font-semibold uppercase text-amber-800 tracking-wider">Commission Slab</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-600">Slab amount ₹</Label>
                <Input type="number" placeholder="e.g. 1000" value={form.commission_slab_amount}
                  onChange={(e) => setForm({ ...form, commission_slab_amount: e.target.value })}
                  data-testid="supplier-slab-amount-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-600">Per-slab commission ₹</Label>
                <Input type="number" placeholder="e.g. 100" value={form.commission_per_slab}
                  onChange={(e) => setForm({ ...form, commission_per_slab: e.target.value })}
                  data-testid="supplier-slab-per-input" />
              </div>
            </div>
            <p className="text-[11px] text-amber-700">
              For every <b>₹{form.commission_slab_amount || "1000"}</b> of sale, supplier earns <b>₹{form.commission_per_slab || "100"}</b>.
              {slabPreview() && <><br />{slabPreview()}</>}
            </p>
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">Fallback commission ₹ (used only if slab = 0)</Label>
            <Input type="number" value={form.default_commission} onChange={(e) => setForm({ ...form, default_commission: e.target.value })} data-testid="supplier-commission-input" />
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">Note</Label>
            <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="min-h-[60px]" />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="sup-active" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
            <label htmlFor="sup-active" className="text-sm">Active</label>
          </div>
          <div className="flex gap-2 pt-3 border-t border-slate-100">
            {!isNew && <Button variant="outline" onClick={del} className="text-red-600 border-red-200" data-testid="delete-supplier-button"><Trash2 className="w-4 h-4 mr-1" /> Delete</Button>}
            <div className="flex-1" />
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-supplier-button">
              <Save className="w-4 h-4 mr-1" /> {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============ REPORTS ============
function KitchenReportsTab() {
  const [summary, setSummary] = useState({ daily: [], by_supplier: [], by_item: [] });
  const [loading, setLoading] = useState(false);
  const [waDate, setWaDate] = useState(todayISO());
  const [waSending, setWaSending] = useState(false);
  const sendWhatsAppSummary = async () => {
    setWaSending(true);
    try {
      const { data } = await axios.get(`${API}/kitchen/reports/whatsapp-summary?date=${waDate}`);
      window.open(data.whatsapp_url, "_blank");
      toast.success("WhatsApp opened — pick a chat to send the day summary");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to build summary"); }
    finally { setWaSending(false); }
  };
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/kitchen/reports/summary`);
        setSummary(data);
      } catch (e) { toast.error("Failed to load reports"); }
      finally { setLoading(false); }
    })();
  }, []);
  const totals = useMemo(() => summary.daily.reduce((a, r) => ({
    total: a.total + r.total, cash: a.cash + r.cash, gpay: a.gpay + r.gpay,
    card: a.card + r.card, commission: a.commission + r.commission,
    profit: a.profit + (r.profit || 0), bills: a.bills + r.bills,
  }), { total: 0, cash: 0, gpay: 0, card: 0, commission: 0, profit: 0, bills: 0 }), [summary]);

  return (
    <div className="space-y-4">
      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[180px]">
          <div className="font-semibold text-emerald-900 text-sm">Daily WhatsApp Summary</div>
          <div className="text-xs text-emerald-700">Total sales, profit &amp; commissions to pay — sent to any chat you pick</div>
        </div>
        <Input type="date" value={waDate} onChange={(e) => setWaDate(e.target.value)} className="h-10 w-40 bg-white" data-testid="wa-summary-date" />
        <Button onClick={sendWhatsAppSummary} disabled={waSending}
          className="bg-emerald-600 hover:bg-emerald-700 text-white h-10" data-testid="wa-summary-button">
          <MessageCircle className="w-4 h-4 mr-2" /> {waSending ? "Building..." : "WhatsApp Summary"}
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        <StatCard label="Total Sales" value={INR(totals.total)} accent="amber" testid="kitchen-report-total" />
        <StatCard label="Profit" value={INR(totals.profit)} accent="emerald" testid="kitchen-report-profit" />
        <StatCard label="Cash" value={INR(totals.cash)} accent="slate" />
        <StatCard label="GPay" value={INR(totals.gpay)} accent="sky" />
        <StatCard label="Card" value={INR(totals.card)} accent="violet" />
        <StatCard label="Commission" value={INR(totals.commission)} accent="red" testid="kitchen-report-commission" />
        <StatCard label="Bills" value={totals.bills} accent="slate" />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100"><h3 className="font-semibold">Daily Summary</h3></div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-right">Bills</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className="px-3 py-2 text-right">Profit</th>
                  <th className="px-3 py-2 text-right">Commission</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={5} className="text-center py-6 text-slate-400">Loading...</td></tr>}
                {!loading && summary.daily.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-slate-400">No sales yet</td></tr>}
                {summary.daily.map((r) => (
                  <tr key={r.date} className="border-t border-slate-100">
                    <td className="px-3 py-2">{r.date}</td>
                    <td className="px-3 py-2 text-right">{r.bills}</td>
                    <td className="px-3 py-2 text-right font-semibold">{INR(r.total)}</td>
                    <td className="px-3 py-2 text-right text-emerald-700 font-semibold">{INR(r.profit)}</td>
                    <td className="px-3 py-2 text-right text-amber-700">{INR(r.commission)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100"><h3 className="font-semibold">By Supplier / Direct Sales</h3></div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Source</th>
                  <th className="px-3 py-2 text-right">Bills</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className="px-3 py-2 text-right">Profit</th>
                  <th className="px-3 py-2 text-right">Commission</th>
                </tr>
              </thead>
              <tbody>
                {summary.by_supplier.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-slate-400">No data</td></tr>}
                {summary.by_supplier.map((r) => (
                  <tr key={r.supplier_id} className="border-t border-slate-100" data-testid={`supplier-summary-${r.supplier_id}`}>
                    <td className="px-3 py-2">{r.supplier_name}</td>
                    <td className="px-3 py-2 text-right">{r.bills}</td>
                    <td className="px-3 py-2 text-right font-semibold">{INR(r.total)}</td>
                    <td className="px-3 py-2 text-right text-emerald-700 font-semibold">{INR(r.profit)}</td>
                    <td className="px-3 py-2 text-right text-amber-700">{INR(r.commission)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100"><h3 className="font-semibold">By Menu Item</h3></div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Item</th>
                <th className="px-3 py-2 text-right">Qty Sold</th>
                <th className="px-3 py-2 text-right">Revenue</th>
                <th className="px-3 py-2 text-right">Profit</th>
              </tr>
            </thead>
            <tbody>
              {(summary.by_item || []).length === 0 && <tr><td colSpan={4} className="text-center py-6 text-slate-400">No item-wise sales yet</td></tr>}
              {(summary.by_item || []).map((r) => (
                <tr key={r.item} className="border-t border-slate-100" data-testid={`item-summary-${r.item}`}>
                  <td className="px-3 py-2">{r.item}</td>
                  <td className="px-3 py-2 text-right">{r.qty}</td>
                  <td className="px-3 py-2 text-right font-semibold">{INR(r.revenue)}</td>
                  <td className="px-3 py-2 text-right text-emerald-700 font-semibold">{INR(r.profit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ApiKeysPanel />
    </div>
  );
}

// ============ API KEYS (standalone app access — admin only) ============
function ApiKeysPanel() {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [newKey, setNewKey] = useState(null);
  const [allowed, setAllowed] = useState(true);
  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/api-keys`);
      setKeys(data.keys || []);
      setAllowed(true);
    } catch (e) {
      if (e.response?.status === 403 || e.response?.status === 401) setAllowed(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!allowed) return null;

  const create = async () => {
    if (!name.trim()) { toast.error("Give the key a name (e.g. Mobile App)"); return; }
    try {
      const { data } = await axios.post(`${API}/admin/api-keys`, { name: name.trim() });
      setNewKey(data);
      setName("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create key"); }
  };
  const revoke = async (k) => {
    if (!window.confirm(`Revoke key "${k.name}"? Apps using it will stop working.`)) return;
    try { await axios.post(`${API}/admin/api-keys/${k.id}/revoke`); toast.success("Key revoked"); load(); }
    catch (e) { toast.error("Failed to revoke"); }
  };
  const copyKey = () => {
    navigator.clipboard.writeText(newKey.api_key).then(() => toast.success("Copied!"));
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="api-keys-panel">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
        <KeyRound className="w-4 h-4 text-amber-600" />
        <h3 className="font-semibold">API Keys (Standalone App Access)</h3>
      </div>
      <div className="p-4 space-y-3">
        <p className="text-xs text-slate-500">
          Create a key for your mobile/standalone app. It can call all <code>/api/kitchen/...</code> endpoints
          with the header <code className="bg-slate-100 px-1 rounded">X-API-Key</code>.
        </p>
        <div className="flex gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Key name e.g. Kitchen Mobile App" className="h-10 flex-1" data-testid="api-key-name-input" />
          <Button onClick={create} className="bg-amber-600 hover:bg-amber-700 text-white h-10" data-testid="create-api-key-button">
            <Plus className="w-4 h-4 mr-1" /> Create Key
          </Button>
        </div>
        {newKey && (
          <div className="border border-emerald-300 bg-emerald-50 rounded-xl p-3" data-testid="new-api-key-box">
            <div className="text-xs font-semibold text-emerald-800 mb-1">
              Key "{newKey.name}" created — COPY IT NOW, it will not be shown again:
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs bg-white border border-emerald-200 rounded-lg px-2 py-1.5 break-all">{newKey.api_key}</code>
              <Button size="icon" variant="outline" onClick={copyKey} className="h-8 w-8 border-emerald-300" data-testid="copy-api-key-button">
                <Copy className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        )}
        <div className="space-y-1.5">
          {keys.map((k) => (
            <div key={k.id} className="flex items-center justify-between border border-slate-100 rounded-xl px-3 py-2 text-sm" data-testid={`api-key-row-${k.id}`}>
              <div className="min-w-0">
                <span className="font-semibold">{k.name}</span>
                <span className="text-xs text-slate-400 ml-2">{k.prefix}</span>
                {k.revoked_at && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold">REVOKED</span>}
              </div>
              {!k.revoked_at && (
                <Button size="sm" variant="ghost" onClick={() => revoke(k)} className="text-red-500 hover:bg-red-50 h-8" data-testid={`revoke-key-${k.id}`}>
                  Revoke
                </Button>
              )}
            </div>
          ))}
          {keys.length === 0 && <div className="text-xs text-slate-400 text-center py-2">No API keys yet</div>}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent = "slate", testid }) {
  const colorMap = {
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    sky: "bg-sky-50 text-sky-800 border-sky-200",
    violet: "bg-violet-50 text-violet-800 border-violet-200",
    red: "bg-red-50 text-red-800 border-red-200",
    slate: "bg-slate-50 text-slate-800 border-slate-200",
  };
  return (
    <div className={`rounded-2xl border p-4 ${colorMap[accent] || colorMap.slate}`} data-testid={testid}>
      <div className="text-xs uppercase tracking-wider font-semibold opacity-80">{label}</div>
      <div className="text-xl font-bold mt-1">{value}</div>
    </div>
  );
}
